import type { RouteType } from "../types/RouteType";
import { routePermissionPath } from "../shared/permissionPath";
import type PageAccessStore from "./PageAccessStore";

/**
 * Hide pages an admin has hidden for this user from the NAV and page-search.
 *
 * `pageAccessRouteGuard` already blocks navigation to a hidden page, but the
 * nav is derived by the library `AppLayoutStore` from `routerStore.routesList`
 * and filters on `route.hidden` — nothing consulted the page-visibility rules,
 * so a hidden page stayed listed in the drawer and the omnibar and only
 * bounced to "Access denied" when clicked. Hiding a hidden page is the entire
 * point of the feature.
 *
 * Since 2026-10-31 HQ has no drawer (AppShell passes enableNavigation={false}):
 * the OmniBar is the only navigation, and every page it lists comes from
 * `routesList` filtered on `route.hidden` (the page index, Favorites, Recent and
 * page search alike). So this getter is what keeps hidden and admin-only pages
 * out of HQ's navigation. Never feed an HQ surface from `leftMenuRoutes`.
 *
 * TWO TRAPS, both load-bearing:
 *
 * 1. `hidden` MUST be a getter, not a copied boolean. The nav surfaces are MobX
 *    computeds that re-read `route.hidden`; MobX converts a getter on an
 *    observable route object into a computed, so the nav re-renders when the
 *    rules land. A boolean snapshot taken at import time never updates — the
 *    rules are not even loaded then. Same reasoning as `views/billing/
 *    billingGate.ts`, which does this for capability gating.
 *
 * 2. This composes with billingGate rather than replacing it. Spreading a route
 *    EVALUATES an inherited `hidden` getter into a static value, which would
 *    freeze billing's visibility at whatever it was during module import. The
 *    getter below therefore reads `route.hidden` from the CLOSURE — the
 *    pre-wrap object — so billing's getter is re-invoked on every read and both
 *    rules apply. Assigning `route.hidden = true` imperatively from the store
 *    would destroy billing's getter outright; never do that.
 *
 * ADMIN-ONLY. The library's `quickNavigationItems` (once the sidebar's "Quick
 * navigation", and the source of the OmniBar's grouped page index) and
 * `omnibarNavigableRoutes` (page search) filter on `route.hidden` but never on
 * `requiresAdmin` — only `leftMenuRoutes` checks it (@intrasync/stores 31.0.1,
 * `AppLayoutStore.js` :943 vs :1234 / :1325). So a non-admin was offered API
 * Keys, Roles, Billing and the rest, and bounced to Access Denied on click. The
 * getter below folds `requiresAdmin` into `hidden`, reading the same admin
 * signal the router refuses with. Found in review of the 31.0.1 adoption,
 * 2026-09-25; the omnibar half predates it.
 *
 * FAIL OPEN. An unresolvable store or unloaded rules means "not hidden". This
 * is deliberate and matches `PageAccessStore`: page visibility is a UI
 * convenience, not a security boundary (the API enforces role/tier), so a
 * missing rule set must not blank someone's nav.
 */

let liveStore: PageAccessStore | null = null;

/** Register the mounted store. Called from the store's own constructor. */
export const registerPageAccessStore = (store: PageAccessStore | null): void => {
  liveStore = store;
};

/** The live store, for the getter below. Null before the RootStore is built. */
export const pageAccessStore = (): PageAccessStore | null => liveStore;

/**
 * Wrap routes so `hidden` also reflects this user's page-visibility rules.
 * Apply LAST, over the fully composed list.
 */
export const hideDeniedRoutes = (
  routes: RouteType[],
  resolveStore: () => PageAccessStore | null = pageAccessStore
): RouteType[] =>
  routes.map((route) => ({
    ...route,
    get hidden(): boolean {
      // Re-reads the ORIGINAL route (see trap 2), so a route that hides itself
      // — or that billingGate hides — stays hidden.
      if (route.hidden === true) return true;
      const store = resolveStore();
      // Admin-only pages leave the nav for everyone else (see ADMIN-ONLY above).
      // Hidden, not fail-open, while the store is unresolved: this only ever
      // withholds a link the router would refuse anyway.
      if (route.requiresAdmin && store?.isAdminUser !== true) return true;
      if (!store) return false;
      return store.isPathDenied(routePermissionPath(route));
    },
  }));

export default hideDeniedRoutes;
